import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { settingsService, ElevenLabsVoice, SavedVoice } from '@/services/settings.service';
import { Search, Play, Pause, Check, Loader2, Mic } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

const MAX_VOICES = 10;

interface Props {
  open: boolean;
  onClose: () => void;
  /** Voices already chosen — pre-selected in the list. */
  initialSelected: SavedVoice[];
  /** Returns the new shortlist (≤10). Parent persists it. */
  onSave: (voices: SavedVoice[]) => void;
}

/**
 * Browse the company's ElevenLabs voice library, preview voices, and pick up
 * to 10 for the shortlist. Built for non-technical users — search by name or
 * accent, hear each voice with one click, tick the ones you want.
 */
export function VoicePickerModal({ open, onClose, initialSelected, onSave }: Props) {
  const [voices, setVoices] = useState<ElevenLabsVoice[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Record<string, SavedVoice>>({});
  const [playing, setPlaying] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setQ('');
    setVoices(null);
    setLoadError(null);
    setSelected(Object.fromEntries(initialSelected.map((v) => [v.voiceId, v])));
    settingsService.listElevenLabsVoices()
      .then(setVoices)
      .catch((e: { response?: { data?: { message?: string } } }) =>
        setLoadError(e?.response?.data?.message ?? 'Could not load voices — check your ElevenLabs key.'));
    return () => { audioRef.current?.pause(); setPlaying(null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filtered = useMemo(() => {
    if (!voices) return [];
    const term = q.trim().toLowerCase();
    if (!term) return voices;
    return voices.filter((v) =>
      v.name.toLowerCase().includes(term)
      || (v.accent ?? '').toLowerCase().includes(term)
      || (v.gender ?? '').toLowerCase().includes(term));
  }, [voices, q]);

  const selectedCount = Object.keys(selected).length;

  const preview = (v: ElevenLabsVoice) => {
    if (!v.previewUrl) { toast('No preview available for this voice', { icon: '🔇' }); return; }
    audioRef.current?.pause();
    if (playing === v.voiceId) { setPlaying(null); return; }
    const a = new Audio(v.previewUrl);
    audioRef.current = a;
    a.onended = () => setPlaying(null);
    a.play().then(() => setPlaying(v.voiceId)).catch(() => setPlaying(null));
  };

  const toggle = (v: ElevenLabsVoice) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[v.voiceId]) {
        delete next[v.voiceId];
      } else {
        if (Object.keys(next).length >= MAX_VOICES) {
          toast.error(`You can keep up to ${MAX_VOICES} voices. Remove one first.`);
          return prev;
        }
        next[v.voiceId] = { voiceId: v.voiceId, name: v.name, accent: v.accent, previewUrl: v.previewUrl };
      }
      return next;
    });
  };

  const save = () => {
    audioRef.current?.pause();
    onSave(Object.values(selected));
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="Choose your voices" widthClass="max-w-2xl">
      <div className="space-y-3">
        <p className="text-xs text-text-muted">
          Pick up to {MAX_VOICES} voices you like. They'll show in a dropdown when you create a voiceover,
          so you never have to remember voice IDs. Tap ▶ to hear each one.
        </p>
        <div className="flex items-center justify-between gap-2">
          <Input
            placeholder="Search by name or accent (e.g. British, calm, female)…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            iconLeft={<Search size={14} />}
          />
          <span className={clsx('shrink-0 text-xs font-medium', selectedCount >= MAX_VOICES ? 'text-warning' : 'text-text-muted')}>
            {selectedCount} / {MAX_VOICES}
          </span>
        </div>

        <div className="max-h-[50vh] space-y-1.5 overflow-y-auto">
          {loadError ? (
            <div className="rounded-lg border border-danger/30 bg-danger-soft/40 px-3 py-3 text-sm text-danger">{loadError}</div>
          ) : voices === null ? (
            [...Array(6)].map((_, i) => <div key={i} className="skeleton h-14 rounded-lg" />)
          ) : filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-text-muted">No voices match “{q}”.</p>
          ) : (
            filtered.map((v) => {
              const isSel = !!selected[v.voiceId];
              const isPlaying = playing === v.voiceId;
              return (
                <div
                  key={v.voiceId}
                  className={clsx(
                    'flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors',
                    isSel ? 'border-primary bg-primary-soft/40' : 'border-border bg-surface hover:bg-surface-alt',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => preview(v)}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-alt text-primary hover:bg-primary-soft"
                    title={v.previewUrl ? 'Preview voice' : 'No preview available'}
                  >
                    {isPlaying ? <Pause size={15} /> : <Play size={15} />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-text-primary">{v.name}</span>
                      {v.accent && <span className="rounded-full bg-surface-alt px-1.5 py-0.5 text-2xs capitalize text-text-muted">{v.accent}</span>}
                      {v.gender && <span className="text-2xs capitalize text-text-muted">{v.gender}</span>}
                    </div>
                    {v.description && <p className="line-clamp-1 text-2xs text-text-muted">{v.description}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => toggle(v)}
                    className={clsx(
                      'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors',
                      isSel ? 'border-primary bg-primary text-on-primary' : 'border-border text-text-muted hover:border-primary/50',
                    )}
                    title={isSel ? 'Remove from your voices' : 'Add to your voices'}
                  >
                    {isSel ? <Check size={14} /> : <Mic size={13} />}
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-border pt-3">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!voices} iconLeft={<Loader2 size={0} className="hidden" />}>
            Save {selectedCount} voice{selectedCount === 1 ? '' : 's'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
